import React from 'react';
import { Redirect } from 'react-router-dom';

import * as paths from './paths';

import { OpenIdLoginCallback } from 'services/auth';
import { Header } from 'pages/App/Header';
import { Footer } from 'pages/App/Footer';
import LoginPage from 'pages/Login';
import AccessDeniedPage from 'pages/Errors/AccessDenied';
import NotFoundPage from 'pages/Errors/NotFound';
import DashboardPage from 'pages/Dashboard';
import FeatureListPage from 'pages/FeatureList';
import FeatureViewPage from 'pages/FeatureView';
import FeatureEditPage from 'pages/FeatureEdit';
import FeatureCreatePage  from 'pages/FeatureCreate';
import ToggleListPage from 'pages/ToggleList';
import RequesterListPage from 'pages/RequesterList';
import RequesterViewPage from 'pages/RequesterView';
import RequesterEditPage from 'pages/RequesterEdit';
import RequesterCreatePage from 'pages/RequesterCreate';
import RequesterTokenListPage from 'pages/RequesterTokenList';
import RequesterTokenViewPage from 'pages/RequesterTokenView';
import RequesterTokenEditPage from 'pages/RequesterTokenEdit';
import RequesterTokenCreatePage from 'pages/RequesterTokenCreate';

const redirectToRequesterListPage: React.FC = () => <Redirect to={paths.buildRequesterListPath()} />;
const OpenIdErrorView: React.FC<{error: Error}> = ({error}) => <LoginPage error={error} />;
const OpenIdLoadingView: React.FC = () => <LoginPage isLoading={true} />;
const OpenIdLoginCallbackPage: React.FC = () => <OpenIdLoginCallback errorView={OpenIdErrorView} loadingView={OpenIdLoadingView} />;

const buildCommonPageRenderer = (Node: React.ReactNode): React.FC => () => <><Header />{Node}<Footer /></>;

interface RouteMap
{
  name: string | undefined,
  path: string | undefined,
  exact?: boolean,
  component: React.ComponentClass<any, any> | React.FunctionComponent<any>,
}

export enum Routes
{
  Initial = 'Dashboard',
  Login = 'Login',
  OpenIdLoginCallback = 'OpenIdLoginCallback',
  AccessDenied = 'AccessDenied',
  NotFound = 'NotFound',
  Dashboard = 'Dashboard',
  FeatureList = 'FeatureList',
  FeatureView = 'FeatureView',
  FeatureCreate = 'FeatureCreate',
  FeatureEdit = 'FeatureEdit',
  ToggleList = 'ToggleList',
  Audience = 'Audience',
  RequesterList = 'RequesterList',
  RequesterView = 'RequesterView',
  RequesterEdit = 'RequesterEdit',
  RequesterCreate = 'RequesterCreate',
  RequesterTokenList = 'RequesterTokenList',
  RequesterTokenView = 'RequesterTokenView',
  RequesterTokenEdit = 'RequesterTokenEdit',
  RequesterTokenCreate = 'RequesterTokenCreate',
};

export const routes: RouteMap[] =
[
  {
    name: Routes.Dashboard,
    path: paths.DASHBOARD_PATH,
    exact: true,
    component: buildCommonPageRenderer(<DashboardPage />),
  },
  {
    name: Routes.Login,
    path: paths.LOGIN_PATH,
    exact: true,
    component: buildCommonPageRenderer(<LoginPage />),
  },
  {
    name: Routes.OpenIdLoginCallback,
    path: paths.OPENID_LOGIN_CALLBACK_PATH,
    exact: true,
    component: buildCommonPageRenderer(<OpenIdLoginCallbackPage />),
  },
  {
    name: Routes.AccessDenied,
    path: paths.ACCESS_DENIED_PATH,
    exact: true,
    component: buildCommonPageRenderer(<AccessDeniedPage />),
  },
  {
    name: Routes.FeatureList,
    path: paths.FEATURE_LIST_PATH,
    exact: true,
    component: buildCommonPageRenderer(<FeatureListPage />),
  },
  {
    name: Routes.FeatureCreate,
    path: paths.FEATURE_CREATE_PATH,
    exact: true,
    component: buildCommonPageRenderer(<FeatureCreatePage />),
  },
  {
    name: Routes.FeatureEdit,
    path: paths.FEATURE_EDIT_PATH,
    exact: true,
    component: buildCommonPageRenderer(<FeatureEditPage />),
  },
  {
    name: Routes.FeatureView,
    path: paths.FEATURE_VIEW_PATH,
    exact: true,
    component: buildCommonPageRenderer(<FeatureViewPage />),
  },
  {
    name: Routes.ToggleList,
    path: paths.TOGGLE_LIST_PATH,
    exact: true,
    component: buildCommonPageRenderer(<ToggleListPage />),
  },
  {
    name: Routes.Audience,
    path: paths.AUDIENCE_PATH,
    exact: true,
    component: redirectToRequesterListPage,
  },
  {
    name: Routes.RequesterList,
    path: paths.REQUESTER_LIST_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterListPage />),
  },
  {
    name: Routes.RequesterCreate,
    path: paths.REQUESTER_CREATE_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterCreatePage />),
  },
  {
    name: Routes.RequesterView,
    path: paths.REQUESTER_VIEW_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterViewPage />),
  },
  {
    name: Routes.RequesterEdit,
    path: paths.REQUESTER_EDIT_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterEditPage />),
  },
  {
    name: Routes.RequesterTokenList,
    path: paths.REQUESTER_TOKEN_LIST_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterTokenListPage />),
  },
  {
    name: Routes.RequesterTokenCreate,
    path: paths.REQUESTER_TOKEN_CREATE_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterTokenCreatePage />),
  },
  {
    name: Routes.RequesterTokenView,
    path: paths.REQUESTER_TOKEN_VIEW_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterTokenViewPage />),
  },
  {
    name: Routes.RequesterTokenEdit,
    path: paths.REQUESTER_TOKEN_EDIT_PATH,
    exact: true,
    component: buildCommonPageRenderer(<RequesterTokenEditPage />),
  },
  {
    name: Routes.NotFound,
    path: undefined,
    component: buildCommonPageRenderer(<NotFoundPage />),
  },
]